import { useState, useEffect } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ShoppingBag, Phone, MapPin, Store } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PICKUP_DISCOUNT = 0.10; // 10%
const DELIVERY_FEE = 2.00;    // €

// Delivery zones — main coverage area. The restaurant may serve nearby towns
// too; customers whose town isn't listed are invited to call to confirm.
const DELIVERY_ZONES = [
  { id: "moralzarzal", name: "Moralzarzal", min: 15 },
  { id: "el-boalo", name: "El Boalo", min: 30 },
  { id: "cerceda", name: "Cerceda", min: 30 },
  { id: "mataelpino", name: "Mataelpino", min: 30 },
  { id: "becerril", name: "Becerril de la Sierra", min: 30 },
  { id: "otras", name: "Otra localidad cercana (a consultar)", min: 30 },
];

export const CartSheet = () => {
  const { lang, cart, cartOpen, setCartOpen, updateQty, removeFromCart, cartTotal, clearCart } = useApp();
  const t = useTranslation(lang);

  const [step, setStep] = useState("cart");
  const [type, setType] = useState("takeout"); // takeout | delivery
  const [zone, setZone] = useState("moralzarzal");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("card"); // card | cash
  const [cashWith, setCashWith] = useState("");
  const [loading, setLoading] = useState(false);

  // Pause Lenis smooth scroll while the sheet is open — keeps the background
  // frozen so scrolling inside the panel doesn't drag the page underneath.
  useEffect(() => {
    const lenis = window.__lenis__;
    if (!lenis) return;
    if (cartOpen) lenis.stop();
    else lenis.start();
    return () => { try { lenis.start(); } catch {} };
  }, [cartOpen]);

  const subtotal = cartTotal;
  const discount = type === "takeout" ? Math.round(subtotal * PICKUP_DISCOUNT * 100) / 100 : 0;
  const shipping = type === "delivery" ? DELIVERY_FEE : 0;
  const total = +(subtotal - discount + shipping).toFixed(2);

  const activeZone = DELIVERY_ZONES.find((z) => z.id === zone) || DELIVERY_ZONES[0];
  const minRequired = activeZone.min;
  const meetsMin = type !== "delivery" || subtotal >= minRequired;

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone) {
      toast.error(lang === "es" ? "Nombre y teléfono son obligatorios." : "Name and phone required.");
      return;
    }
    if (type === "delivery" && !address) {
      toast.error(lang === "es" ? "Indica una dirección." : "Please add an address.");
      return;
    }
    if (!meetsMin) {
      toast.error(t.order.minWarning);
      return;
    }
    setLoading(true);
    try {
      const orderNotes = [
        notes,
        type === "delivery" ? `Zona: ${activeZone.name} (min ${activeZone.min}€)` : "",
        type === "takeout" ? "Recogida en local (-10%)" : "",
        payment === "cash"
          ? `Pago: EFECTIVO${cashWith.trim() ? ` (paga con ${cashWith.trim()} €)` : ""}`
          : "Pago: TARJETA",
      ].filter(Boolean).join(" · ");
      await axios.post(`${API}/orders`, {
        customer_name: name, phone, email: email || null,
        type, address, notes: orderNotes,
        items: cart.map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        total,
      });
      toast.success(t.order.success);
      clearCart();
      setStep("cart");
      setCartOpen(false);
      setName(""); setPhone(""); setEmail(""); setAddress(""); setNotes("");
    } catch {
      toast.error(t.order.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent
        data-testid="cart-sheet"
        side="right"
        className="bg-[#0A0A0A] border-l border-q5 text-white w-full sm:max-w-md p-0 flex flex-col overflow-hidden"
      >
        <SheetHeader className="px-6 py-5 border-b border-q5">
          <SheetTitle className="font-serif-q5 text-2xl text-white flex items-center gap-2">
            <ShoppingBag size={20} className="text-gold" />
            {t.order.title}
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-neutral-500 px-6">
            <ShoppingBag size={48} className="mb-4 opacity-30" />
            <p>{t.order.empty}</p>
          </div>
        ) : step === "cart" ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
              {cart.map((item) => (
                <div key={item.id} data-testid={`cart-item-${item.id}`} className="flex items-start gap-3 border-b border-q5 pb-4">
                  <div className="flex-1">
                    <p className="text-white text-sm leading-snug">{item.name}</p>
                    <p className="text-gold text-sm mt-1">{(item.price * item.quantity).toFixed(2)} €</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`cart-dec-${item.id}`} onClick={() => updateQty(item.id, -1)} className="h-7 w-7 border border-q5 hover:border-gold flex items-center justify-center transition-colors"><Minus size={12} /></button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <button data-testid={`cart-inc-${item.id}`} onClick={() => updateQty(item.id, 1)} className="h-7 w-7 border border-q5 hover:border-gold flex items-center justify-center transition-colors"><Plus size={12} /></button>
                  </div>
                  <button data-testid={`cart-remove-${item.id}`} onClick={() => removeFromCart(item.id)} className="text-neutral-500 hover:text-[#A91D11] transition-colors ml-1"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="px-6 py-5 border-t border-q5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="overline">{t.order.subtotal}</span>
                <span data-testid="cart-total" className="font-serif-q5 text-3xl text-gold">{subtotal.toFixed(2)} €</span>
              </div>
              <button data-testid="cart-checkout-btn" onClick={() => setStep("checkout")} className="btn-primary-q5 w-full">{t.order.checkout}</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} data-testid="checkout-form" className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
              <div data-testid="order-call-notice" className="border-l-2 border-gold pl-3 py-2 bg-[#A91D11]/10">
                <p className="text-[12px] text-gold/90 leading-relaxed">{t.order.callNotice}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-white text-xs">
                  {PHONES.map((p) => (
                    <a key={p.tel} href={`tel:${p.tel}`} className="hover:text-gold transition-colors flex items-center gap-1">
                      <Phone size={10} className="text-gold" /> {p.display}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <p className="overline mb-2">{t.order.type}</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="order-type-takeout"
                    onClick={() => setType("takeout")}
                    className={`py-3 px-2 text-xs uppercase tracking-[0.15em] border transition-colors text-left flex flex-col gap-1 ${type === "takeout" ? "border-gold bg-gold/10 text-gold" : "border-q5 text-neutral-400 hover:border-gold"}`}
                  >
                    <Store size={14} />
                    <span className="font-semibold leading-tight">{t.order.takeout}</span>
                    <span className="text-[10px] opacity-80">{t.order.pickupDiscount}</span>
                  </button>
                  <button
                    type="button"
                    data-testid="order-type-delivery"
                    onClick={() => setType("delivery")}
                    className={`py-3 px-2 text-xs uppercase tracking-[0.15em] border transition-colors text-left flex flex-col gap-1 ${type === "delivery" ? "border-[#A91D11] bg-[#A91D11]/20 text-white" : "border-q5 text-neutral-400 hover:border-gold"}`}
                  >
                    <MapPin size={14} />
                    <span className="font-semibold leading-tight">{t.order.delivery}</span>
                    <span className="text-[10px] opacity-80">{t.order.deliveryFee}</span>
                  </button>
                </div>
                {type === "takeout" && (
                  <p data-testid="pickup-hint" className="text-[11px] text-gold mt-2">{t.order.pickupHint}</p>
                )}
                {type === "delivery" && (
                  <div className="mt-3 space-y-2">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">{t.order.selectZone}</p>
                    <Select value={zone} onValueChange={setZone}>
                      <SelectTrigger data-testid="zone-select" className="h-11 bg-transparent border-q5 rounded-sm text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-q5 text-white">
                        {DELIVERY_ZONES.map((z) => (
                          <SelectItem
                            key={z.id}
                            value={z.id}
                            data-testid={`zone-option-${z.id}`}
                            className="focus:bg-[#A91D11]/30"
                          >
                            {z.name} · mín {z.min} €
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Input data-testid="order-name" placeholder={t.order.name + " *"} value={name} onChange={(e) => setName(e.target.value)} required className="bg-transparent border-q5 h-12 focus-visible:ring-gold rounded-sm" />
              <Input data-testid="order-phone" placeholder={t.order.phone + " *"} value={phone} onChange={(e) => setPhone(e.target.value)} required className="bg-transparent border-q5 h-12 focus-visible:ring-gold rounded-sm" />
              <Input data-testid="order-email" type="email" placeholder={t.order.email} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-transparent border-q5 h-12 focus-visible:ring-gold rounded-sm" />
              {type === "delivery" && (
                <Input data-testid="order-address" placeholder={t.order.address + " *"} value={address} onChange={(e) => setAddress(e.target.value)} required className="bg-transparent border-q5 h-12 focus-visible:ring-gold rounded-sm" />
              )}

              <div>
                <p className="overline mb-2">Método de pago</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="payment-card"
                    onClick={() => setPayment("card")}
                    className={`min-h-[48px] py-3 px-3 text-xs uppercase tracking-[0.15em] border transition-colors ${payment === "card" ? "border-gold bg-gold/10 text-gold" : "border-q5 text-neutral-400 hover:border-gold"}`}
                  >
                    Tarjeta
                  </button>
                  <button
                    type="button"
                    data-testid="payment-cash"
                    onClick={() => setPayment("cash")}
                    className={`min-h-[48px] py-3 px-3 text-xs uppercase tracking-[0.15em] border transition-colors ${payment === "cash" ? "border-gold bg-gold/10 text-gold" : "border-q5 text-neutral-400 hover:border-gold"}`}
                  >
                    Efectivo
                  </button>
                </div>
                {payment === "cash" && (
                  <Input
                    data-testid="payment-cash-with"
                    type="number"
                    step="0.01"
                    min="0"
                    inputMode="decimal"
                    placeholder="¿Con cuánto pagas? (opcional)"
                    value={cashWith}
                    onChange={(e) => setCashWith(e.target.value)}
                    className="mt-2 bg-transparent border-q5 h-12 focus-visible:ring-gold rounded-sm"
                  />
                )}
              </div>

              <Textarea data-testid="order-notes" placeholder={t.order.notes} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-transparent border-q5 focus-visible:ring-gold rounded-sm" />
            </div>
            <div className="px-6 py-5 border-t border-q5 space-y-2.5">
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-neutral-400">
                  <span>{t.order.subtotal}</span>
                  <span data-testid="checkout-subtotal">{subtotal.toFixed(2)} €</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-gold">
                    <span>{t.order.discount}</span>
                    <span data-testid="checkout-discount">−{discount.toFixed(2)} €</span>
                  </div>
                )}
                {shipping > 0 && (
                  <div className="flex justify-between text-neutral-300">
                    <span>{t.order.shipping}</span>
                    <span data-testid="checkout-shipping">+{shipping.toFixed(2)} €</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-q5">
                <span className="overline">{t.order.total}</span>
                <span data-testid="checkout-total" className="font-serif-q5 text-3xl text-gold">{total.toFixed(2)} €</span>
              </div>
              {!meetsMin && (
                <p data-testid="min-warning" className="text-[11px] text-[#FF8B6B]">{t.order.minWarning}</p>
              )}
              <div className="grid grid-cols-[auto_1fr] gap-2 pt-1">
                <button
                  type="button"
                  data-testid="order-back-btn"
                  onClick={() => setStep("cart")}
                  className="px-4 py-3 text-xs uppercase tracking-[0.2em] border border-q5 text-neutral-300 hover:border-gold hover:text-gold transition-colors flex items-center justify-center gap-1.5"
                >
                  ← {t.order.back}
                </button>
                <button
                  type="submit"
                  data-testid="order-submit-btn"
                  disabled={loading || !meetsMin}
                  className="btn-primary-q5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "..." : t.order.submit}
                </button>
              </div>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};
